import {
  장례준비긴급도_LIST,
  조문객예상인원_LIST,
} from "@/containers/MatchContainer/constants/request";
import { ParlorEstimateCard } from "@/containers/Parlor/ParlorDetailContainer/components/ParlorEstimateCard";
import { useParlorCompareEstimate } from "@/containers/Parlor/ParlorDetailContainer/hooks/useParlorCompareEstimate";
import { useCustomVariation } from "@/hooks/useCustomVariation";
// import { useCustomVariation } from "@/hooks/useCustomVariation";
import { Box, Button, Flex, HStack, VStack } from "@chakra-ui/react";
import {
  call,
  ColorIcon,
  colors,
  customEvent,
  // Dropdown,
  Field,
  FUNERAL_SIDO_LIST,
  FUNERAL_SIGUNGU,
  HackleEventName,
} from "goi_common";
import { Dispatch, Reducer, useEffect, useMemo, useReducer, useState } from "react";
import AllQuotationRequestButtonGroup from "../AllQuotationRequest/AllQuotationRequestButtonGroup";

export default function RequestQuestionStepByStep() {
  const [currentStep, setCurrentStep] = useState(1);

  const steps = useMemo(() => {
    return [
      {
        step: 1,
      },

      {
        step: 2,
      },

      {
        step: 3,
      },

      {
        step: 4,
      },
    ];
  }, []);

  const stepTitle = useMemo(() => {
    const titles = [
      "예상 조문객 수를\n선택해주세요",
      "어디서 장례를 치를 계획이신가요?",
      "장례 준비 상황을\n 자세히 알려주세요",
    ];
    return titles[currentStep - 1];
  }, [currentStep]);

  const [selectedOption, setSelectedOption] = useReducer<
    Reducer<SelectedOption, Partial<SelectedOption>>
  >(
    (prev, next) => {
      return { ...prev, ...next };
    },
    {
      무의미: "",
      장례준비긴급도: "",
      sido: null,
      sigungu: null,
      조문객수: "",
    },
  );

  const isNextDisabled = useMemo(() => {
    if (currentStep === 1) {
      return Boolean(!selectedOption.조문객수);
    } else if (currentStep === 2) {
      return !Boolean(selectedOption.sido && selectedOption.sigungu);
    } else {
      return Boolean(!selectedOption.장례준비긴급도);
    }
  }, [selectedOption, currentStep]);

  return (
    <VStack px="16px">
      <HeaderLayout title={`장례 비용 알아보기`} />
      <StepIndicator steps={steps} currentStep={currentStep} />

      <Box pt="24px" width="100%" whiteSpace="pre-line" className="head2_b black">
        {stepTitle}
      </Box>
      <Box pt="24px" width="100%">
        <StepQuestion
          currentStep={currentStep}
          selectedOption={selectedOption}
          setSelectedOption={setSelectedOption}
        />
      </Box>

      <HStack pt="24px" width="100%" alignItems="center" justifyContent="space-between" gap="24px">
        <Box width="100%">
          <PrevNext
            disabled={currentStep <= 1}
            text="이전"
            onClick={() => setCurrentStep(currentStep - 1)}
          />
        </Box>

        <Box width="100%">
          {currentStep === 3 ? (
            <AllQuotationRequestButtonGroup
              isTesting
              selectedOption={selectedOption}
              isUniqueButton
              disabled={isNextDisabled}
            />
          ) : (
            <PrevNext
              disabled={isNextDisabled}
              text="다음"
              onClick={() => {
                setCurrentStep(currentStep + 1);
              }}
            />
          )}
        </Box>
      </HStack>

      <Box pt="24px" pb="120px" width="100%">
        <StepQuestionExtraDetail currentStep={currentStep} />
      </Box>
    </VStack>
  );
}

const PrevNext = ({
  text,
  disabled,
  onClick,
}: {
  text: string;
  disabled?: boolean;
  onClick: () => void;
}) => {
  return (
    <Button
      onClick={onClick}
      isDisabled={disabled}
      background={colors.orange650}
      color="white"
      _hover={{ background: colors.orange500 }}
      _disabled={{
        background: colors.orange400,
      }}
      borderRadius="36px"
      width="100%"
    >
      {text}
    </Button>
  );
};

const SIDO_LIST = FUNERAL_SIDO_LIST.map((sido) => ({
  key: sido[1] as string,
  value: sido[0],
}));

export interface SelectedOption {
  무의미?: string;
  장례준비긴급도: string;
  sido: null | number;
  sigungu: null | number;
  조문객수: string;
}

export const StepQuestion = ({
  currentStep,
  selectedOption,
  setSelectedOption,
}: {
  currentStep: number;
  selectedOption: SelectedOption;
  setSelectedOption: Dispatch<Partial<SelectedOption>>;
}) => {
  const [regionStep, setRegionStep] = useState(0);

  const sigunguValues = useMemo(() => {
    if (!selectedOption.sido) {
      return [];
    }

    const currentSigunguList = Array.from(FUNERAL_SIGUNGU.get(selectedOption.sido)!);

    return currentSigunguList.map((sigungu) => {
      const [code, name] = sigungu;

      return {
        key: name,
        value: code,
      };
    });
  }, [selectedOption.sido]);

  useEffect(() => {
    if (currentStep === 1) {
      setRegionStep(1);
    }
  }, [currentStep]);

  if (currentStep === 1) {
    return (
      <VStack width="100%">
        <Field.ButtonGroups
          items={조문객예상인원_LIST}
          value={selectedOption.조문객수}
          onChange={(e) => {
            setSelectedOption({ 조문객수: e });
            customEvent({
              newGtm: {
                path: "match_request",
                trigger_and_target: "condolers-answer",
              },
              hackle: {
                key: HackleEventName["견적 요청 - 조문객 수 선택"],
                properties: { name: String(e) },
              },
            });
          }}
          borderColor={colors.white}
          buttonWidth="100%"
        />
      </VStack>
    );
  } else if (currentStep === 2) {
    return (
      <VStack width="100%" alignItems="start" pb="64px">
        {regionStep === 1 && (
          <Flex flexWrap="wrap" width="100%">
            {SIDO_LIST.map((sido) => {
              return (
                <SelectBox
                  key={sido.key}
                  onClick={() => {
                    const v = sido.value;
                    setSelectedOption({ sido: v, sigungu: undefined });
                    customEvent({
                      newGtm: {
                        path: "match_request",
                        trigger_and_target: "sido-answer",
                      },
                      hackle: {
                        key: HackleEventName["견적 요청 - 장례 시도 선택"],
                        properties: { name: String(v) },
                      },
                    });
                    setRegionStep(2);
                  }}
                  isSelected={sido.value === selectedOption.sido}
                >
                  {sido.key}
                </SelectBox>
              );
            })}
          </Flex>
        )}
        {regionStep === 2 && (
          <Flex flexWrap="wrap" width="100%">
            {sigunguValues.map((sigungu) => {
              return (
                <SelectBox
                  key={sigungu.key}
                  onClick={() => {
                    const v = sigungu.value;

                    setSelectedOption({ sigungu: v });
                    customEvent({
                      newGtm: {
                        path: "match_request",
                        trigger_and_target: "sigungu-answer",
                      },
                      hackle: {
                        key: HackleEventName["견적 요청 - 장례 시군구 선택"],
                        properties: { name: String(v) },
                      },
                    });
                  }}
                  isSelected={sigungu.value === selectedOption.sigungu}
                >
                  {sigungu.key}
                </SelectBox>
              );
            })}
          </Flex>
        )}
      </VStack>
    );
  } else if (currentStep === 3) {
    return (
      <VStack width="100%">
        <Field.ButtonGroups
          items={장례준비긴급도_LIST}
          value={selectedOption.장례준비긴급도}
          onChange={(e) => {
            setSelectedOption({ 장례준비긴급도: e });
            customEvent({
              newGtm: {
                path: "match_request",
                trigger_and_target: "status-answer",
              },
              hackle: {
                key: HackleEventName["견적 요청 - 장례준비긴급도 선택"],
                properties: { name: e },
              },
            });
          }}
          buttonWidth="100%"
          borderColor="white"
        />
      </VStack>
    );
  } else return null;
};

const StepQuestionExtraDetail = ({ currentStep }: { currentStep: number }) => {
  const { estimates } = useParlorCompareEstimate({
    isDetail: false,
    sido: String(11),
    sigungu: "",
    isLargeCity: false,
  });
  if (!currentStep) {
    return null;
  }
  if (currentStep === 1) {
    return (
      <VStack width="100%" borderRadius="6px" background={colors.warmGray100} padding="12px">
        <Box className="subtitle_14_b gray600" width="100%">
          <span className="danger">맞춤형 장례 예상 비용</span>부터{" "}
          <span className="danger">할인혜택</span>까지 한번에 알아볼 수 있어요.
          <br />
          <span className="caption_12_b gray500">
            * 실시간 견적 알아보기를 통해 상세비용, 최대 100% 빈소 할인까지 알아볼 수 있습니다.
          </span>
        </Box>
        {estimates.answered && estimates.answered.length > 0 && (
          <VStack width="100%" background={colors.blue150} padding="16px 20px" gap={1}>
            <Box width="100%" className="caption_12_b gray800">
              견적 결과 예시
            </Box>
            {estimates.answered.slice(0, 2).map((item) => (
              <ParlorEstimateCard
                key={String(item.id) + "_card"}
                type="answered"
                item={item}
                hasLink={false}
              />
            ))}
          </VStack>
        )}
      </VStack>
    );
  } else if (currentStep === 2) {
    return (
      <VStack width="100%" borderRadius="6px" background={colors.warmGray100} padding="12px">
        <Box className="subtitle_14_b gray600" width="100%">
          <span className="caption_12_b gray500">
            전체 장례 비용부터 지역별 장례 비용 차이, 추천 장례식장 비용을 알려드려요
          </span>
        </Box>
      </VStack>
    );
  } else if (currentStep === 3) {
    return (
      <VStack width="100%" borderRadius="6px" background={colors.warmGray100} padding="12px">
        <Box width="100%" className="body_14_m gray800" pb="36px">
          장례 비용 뿐만 아니라 전반적인 장례 절차, 임종접수, 긴급한 문의가 있으시면 부담없이 전화나
          채팅으로 연락주세요.
        </Box>
        <HStack width="100%">
          <Button
            flexGrow={1}
            style={{
              borderRadius: 6,
              backgroundColor: colors.gray800,
              padding: "6px 14px",
              boxShadow: "2px 2px 2px #00000022",
            }}
            onClick={() => {
              customEvent({
                newGtm: {
                  path: "match_request",
                  trigger_and_target: "quick_cnslt-opn_urgnt_mdl",
                },
              });
              call("1666-9784");
            }}
          >
            <HStack alignItems="center" justifyContent="center" gap={1}>
              <ColorIcon name="icon-call-mono" color={colors.white} width={12} height={12} />
              <div className="caption_12_sb white">전화 상담</div>{" "}
            </HStack>
          </Button>
          <Button
            flexGrow={1}
            style={{
              borderRadius: 8,
              backgroundColor: colors.gray800,
              padding: "6px 14px",
              boxShadow: "2px 2px 2px #00000022",
            }}
            onClick={() => {
              customEvent({
                newGtm: {
                  path: "match_request",
                  trigger_and_target: "chat",
                },
              });
              window.open("http://pf.kakao.com/_xgFDaK/chat");
            }}
          >
            <HStack alignItems="center" justifyContent="center" gap={1}>
              <ColorIcon name="chat_bubble_rounded" color={colors.white} width={12} height={12} />
              <div className="caption_12_sb white">채팅 상담</div>{" "}
            </HStack>
          </Button>
        </HStack>
      </VStack>
    );
  } else return null;
};

export const StepIndicator = ({ steps, currentStep }: { steps: any[]; currentStep: number }) => {
  return (
    <HStack width="100%" alignItems="center" justifyContent="center" gap={1} paddingRight="0px">
      {steps.map((step, index) => {
        return (
          <Box
            key={step}
            width="8px"
            height="8px"
            borderRadius="50%"
            backgroundColor={currentStep === index + 1 ? colors.gray600 : colors.gray300}
          ></Box>
        );
      })}
    </HStack>
  );
};

export const HeaderLayout = ({ title = "장례 비용 알아보기" }: { title: string }) => {
  return (
    <>
      <VStack gap="10px" padding="24px 0 0" width="100%">
        <HStack alignItems="center" justifyContent="center">
          <Box className="body_14_m gray500">{title}</Box>
          {/* <VStack gap={10}>
            <Button
              style={{
                borderRadius: 16,
                backgroundColor: colors.orange100,
                padding: "6px 14px",
                boxShadow: "2px 2px 2px #00000022",
              }}
              onClick={() => {
                customEvent({
                  newGtm: {
                    path: "match_request",
                    trigger_and_target: "quick_cnslt-opn_urgnt_mdl",
                  },
                });
                onOpen();
              }}
            >
              <HStack alignItems="center" gap={10}>
                <ColorIcon name="icon-call-mono" color={colors.orange650} width={12} height={12} />
                <div className="caption_12_sb orange700">빠른 상담</div>{" "}
                <ColorIcon name="icon-arrow-right-small-mono" color={colors.orange700} size={8} />
              </HStack>
            </Button>
          </VStack> */}
        </HStack>
      </VStack>
    </>
  );
};

const SelectBox = (props: any) => {
  const children = props.children;
  const isSelected = props.isSelected;
  return (
    <Box
      {...props}
      width="33%"
      height="48px"
      border={`0.5px solid ${colors.gray300}`}
      textAlign="center"
      className="body_16_m gray700"
      lineHeight="48px"
      background={isSelected ? colors.black : ""}
      color={isSelected ? colors.white : ""}
      _hover={{
        background: isSelected ? "" : colors.warmGray200,
        cursor: "pointer",
      }}
    >
      {children}
    </Box>
  );
};

export function RequestQuestionStepByStepB() {
  const [currentStep, setCurrentStep] = useState(1);
  const variation = useCustomVariation(128);

  const steps = useMemo(() => {
    return [
      {
        step: 1,
      },

      {
        step: 2,
      },

      {
        step: 3,
      },

      {
        step: 4,
      },
    ];
  }, []);

  const stepTitle = useMemo(() => {
    const titles = [
      "어디에서 장례를 치르고자하시나요?",
      "예상 조문객 수를\n선택해주세요",
      "장례 준비 상황을\n 자세히 알려주세요",
    ];
    return titles[currentStep - 1];
  }, [currentStep]);

  const [selectedOption, setSelectedOption] = useReducer<
    Reducer<SelectedOption, Partial<SelectedOption>>
  >(
    (prev, next) => {
      return { ...prev, ...next };
    },
    {
      무의미: "",
      장례준비긴급도: "",
      sido: null,
      sigungu: null,
      조문객수: "",
    },
  );

  const isNextDisabled = useMemo(() => {
    if (currentStep === 1) {
      return !Boolean(selectedOption.sido && selectedOption.sigungu);
    } else if (currentStep === 2) {
      return Boolean(!selectedOption.조문객수);
    } else {
      return Boolean(!selectedOption.장례준비긴급도);
    }
  }, [selectedOption, currentStep]);

  const [regionStep, setRegionStep] = useState(0);

  const isInitialStepAndSelectedSido = currentStep <= 1 && regionStep <= 1;
  const isInitialStepAndSelectedSidoSigungu = currentStep <= 1 && regionStep <= 2;

  return (
    <VStack px="16px">
      <HeaderLayout title={`장례 비용 알아보기`} />
      <StepIndicator steps={steps} currentStep={currentStep} />

      <Box pt="12px" width="100%" whiteSpace="pre-line" className="head2_b black" pb="12px">
        {stepTitle}
      </Box>
      {variation === "B" && currentStep === 1 && (
        <VStack width="100%" borderRadius="2px" background={colors.warmGray150} padding="16px">
          <Box width="100%" className="body_14_m gray800" textAlign="center">
            <b>빠른 도움이 필요하시면</b>
            <br />
            바로 연락주셔도 괜찮아요
          </Box>
          <HStack width="100%">
            <Button
              width="100%"
              flexGrow={1}
              style={{
                borderRadius: "4px",
                backgroundColor: colors.warmGray750,
                padding: "6px 14px",
              }}
              onClick={() => {
                customEvent({
                  newGtm: {
                    path: "match_request",
                    trigger_and_target: "chat",
                  },
                });
                window.open("http://pf.kakao.com/_xgFDaK/chat");
              }}
            >
              <HStack alignItems="center" justifyContent="center" gap={1}>
                <ColorIcon name="chat_bubble_rounded" color={colors.white} width={12} height={12} />
                <div className="caption_12_sb white">채팅 상담</div>{" "}
              </HStack>
            </Button>
            <Button
              width="100%"
              flexGrow={1}
              style={{
                borderRadius: "4px",
                backgroundColor: colors.warmGray800,
                padding: "6px 14px",
              }}
              onClick={() => {
                customEvent({
                  newGtm: {
                    path: "match_request",
                    trigger_and_target: "quick_cnslt-opn_urgnt_mdl",
                  },
                });
                call("1666-9784");
              }}
            >
              <HStack alignItems="center" justifyContent="center" gap={1}>
                <ColorIcon name="icon-call-mono" color={colors.white} width={12} height={12} />
                <div className="caption_12_sb white">24시간 상담전화</div>{" "}
              </HStack>
            </Button>
          </HStack>
        </VStack>
      )}

      <Box pt="24px" width="100%">
        <StepQuestionB
          currentStep={currentStep}
          selectedOption={selectedOption}
          setSelectedOption={setSelectedOption}
          regionStep={regionStep}
          setRegionStep={setRegionStep}
        />
      </Box>

      <HStack pt="24px" width="100%" alignItems="center" justifyContent="space-between" gap="24px">
        <Box width="100%">
          <PrevNext
            disabled={isInitialStepAndSelectedSido}
            text="이전"
            onClick={() => {
              if (isInitialStepAndSelectedSidoSigungu) {
                setRegionStep(1);
                return;
              }
              setCurrentStep(currentStep - 1);
            }}
          />
        </Box>

        <Box width="100%">
          {currentStep === 3 ? (
            <AllQuotationRequestButtonGroup
              isTesting
              selectedOption={selectedOption}
              isUniqueButton
              disabled={isNextDisabled}
            />
          ) : (
            <PrevNext
              disabled={isNextDisabled}
              text="다음"
              onClick={() => {
                setCurrentStep(currentStep + 1);
              }}
            />
          )}
        </Box>
      </HStack>

      <Box pt="24px" pb="120px" width="100%">
        <StepQuestionExtraDetailB currentStep={currentStep} />
      </Box>
    </VStack>
  );
}

export const StepQuestionB = ({
  currentStep,
  selectedOption,
  setSelectedOption,
  regionStep,
  setRegionStep,
}: {
  currentStep: number;
  selectedOption: SelectedOption;
  setSelectedOption: Dispatch<Partial<SelectedOption>>;
  regionStep: number;
  setRegionStep: Dispatch<number>;
}) => {
  const sigunguValues = useMemo(() => {
    if (!selectedOption.sido) {
      return [];
    }

    const currentSigunguList = Array.from(FUNERAL_SIGUNGU.get(selectedOption.sido)!);

    return currentSigunguList.map((sigungu) => {
      const [code, name] = sigungu;

      return {
        key: name,
        value: code,
      };
    });
  }, [selectedOption.sido]);

  useEffect(() => {
    if (currentStep === 1) {
      setRegionStep(1);
    }
  }, [currentStep]);

  if (currentStep === 1) {
    return (
      <VStack width="100%" alignItems="start" pb="64px">
        {regionStep === 1 && (
          <Flex flexWrap="wrap" width="100%">
            {SIDO_LIST.map((sido) => {
              return (
                <SelectBox
                  key={sido.key}
                  onClick={() => {
                    const v = sido.value;
                    setSelectedOption({ sido: v, sigungu: undefined });
                    customEvent({
                      newGtm: {
                        path: "match_request",
                        trigger_and_target: "sido-answer",
                      },
                      hackle: {
                        key: HackleEventName["견적 요청 - 장례 시도 선택"],
                        properties: { name: String(v) },
                      },
                    });
                    setRegionStep(2);
                  }}
                  isSelected={sido.value === selectedOption.sido}
                >
                  {sido.key}
                </SelectBox>
              );
            })}
          </Flex>
        )}
        {regionStep === 2 && (
          <Flex flexWrap="wrap" width="100%">
            {sigunguValues.map((sigungu) => {
              return (
                <SelectBox
                  key={sigungu.key}
                  onClick={() => {
                    const v = sigungu.value;

                    setSelectedOption({ sigungu: v });
                    customEvent({
                      newGtm: {
                        path: "match_request",
                        trigger_and_target: "sigungu-answer",
                      },
                      hackle: {
                        key: HackleEventName["견적 요청 - 장례 시군구 선택"],
                        properties: { name: String(v) },
                      },
                    });
                  }}
                  isSelected={sigungu.value === selectedOption.sigungu}
                >
                  {sigungu.key}
                </SelectBox>
              );
            })}
          </Flex>
        )}
      </VStack>
    );
  } else if (currentStep === 2) {
    return (
      <VStack width="100%">
        <Field.ButtonGroups
          items={조문객예상인원_LIST}
          value={selectedOption.조문객수}
          onChange={(e) => {
            setSelectedOption({ 조문객수: e });
            customEvent({
              newGtm: {
                path: "match_request",
                trigger_and_target: "condolers-answer",
              },
              hackle: {
                key: HackleEventName["견적 요청 - 조문객 수 선택"],
                properties: { name: String(e) },
              },
            });
          }}
          borderColor={colors.white}
          buttonWidth="100%"
        />
      </VStack>
    );
  } else if (currentStep === 3) {
    return (
      <VStack width="100%">
        <Field.ButtonGroups
          items={장례준비긴급도_LIST}
          value={selectedOption.장례준비긴급도}
          onChange={(e) => {
            setSelectedOption({ 장례준비긴급도: e });
            customEvent({
              newGtm: {
                path: "match_request",
                trigger_and_target: "status-answer",
              },
              hackle: {
                key: HackleEventName["견적 요청 - 장례준비긴급도 선택"],
                properties: { name: e },
              },
            });
          }}
          buttonWidth="100%"
          borderColor="white"
        />
      </VStack>
    );
  } else return null;
};

const StepQuestionExtraDetailB = ({ currentStep }: { currentStep: number }) => {
  const { estimates } = useParlorCompareEstimate({
    isDetail: false,
    sido: String(11),
    sigungu: "",
    isLargeCity: false,
  });

  if (!currentStep) {
    return null;
  }

  if (currentStep === 1) {
    return (
      <VStack width="100%" borderRadius="6px" background={colors.warmGray100} padding="12px">
        <Box className="subtitle_14_b gray600" width="100%">
          <span className="caption_12_b gray500">
            전체 장례 비용부터 지역별 장례 비용 차이, 추천 장례식장 비용을 알려드려요
          </span>
        </Box>
      </VStack>
    );
  } else if (currentStep === 2) {
    return (
      <VStack width="100%" borderRadius="6px" background={colors.warmGray100} padding="12px">
        <Box className="subtitle_14_b gray600" width="100%">
          <span className="danger">맞춤형 장례 예상 비용</span>부터{" "}
          <span className="danger">할인혜택</span>까지 한번에 알아볼 수 있어요.
          <br />
          <span className="caption_12_b gray500">
            * 실시간 견적 알아보기를 통해 상세비용, 최대 100% 빈소 할인까지 알아볼 수 있습니다.
          </span>
        </Box>
        {estimates.answered && estimates.answered.length > 0 && (
          <VStack width="100%" background={colors.blue150} padding="16px 20px" gap={1}>
            <Box width="100%" className="caption_12_b gray800">
              견적 결과 예시
            </Box>
            {estimates.answered.slice(0, 2).map((item) => (
              <ParlorEstimateCard
                key={String(item.id) + "_card"}
                type="answered"
                item={item}
                hasLink={false}
              />
            ))}
          </VStack>
        )}
      </VStack>
    );
  } else if (currentStep === 3) {
    return (
      <VStack width="100%" borderRadius="6px" background={colors.warmGray100} padding="12px">
        <Box width="100%" className="body_14_m gray800" pb="36px">
          장례 비용 뿐만 아니라 전반적인 장례 절차, 임종접수, 긴급한 문의가 있으시면 부담없이 전화나
          채팅으로 연락주세요.
        </Box>
        <HStack width="100%">
          <Button
            flexGrow={1}
            style={{
              borderRadius: 6,
              backgroundColor: colors.gray800,
              padding: "6px 14px",
              boxShadow: "2px 2px 2px #00000022",
            }}
            onClick={() => {
              customEvent({
                newGtm: {
                  path: "match_request",
                  trigger_and_target: "quick_cnslt-opn_urgnt_mdl",
                },
              });
              call("1666-9784");
            }}
          >
            <HStack alignItems="center" justifyContent="center" gap={1}>
              <ColorIcon name="icon-call-mono" color={colors.white} width={12} height={12} />
              <div className="caption_12_sb white">전화 상담</div>{" "}
            </HStack>
          </Button>
          <Button
            flexGrow={1}
            style={{
              borderRadius: 8,
              backgroundColor: colors.gray800,
              padding: "6px 14px",
              boxShadow: "2px 2px 2px #00000022",
            }}
            onClick={() => {
              customEvent({
                newGtm: {
                  path: "match_request",
                  trigger_and_target: "chat",
                },
              });
              window.open("http://pf.kakao.com/_xgFDaK/chat");
            }}
          >
            <HStack alignItems="center" justifyContent="center" gap={1}>
              <ColorIcon name="chat_bubble_rounded" color={colors.white} width={12} height={12} />
              <div className="caption_12_sb white">채팅 상담</div>{" "}
            </HStack>
          </Button>
        </HStack>
      </VStack>
    );
  } else return null;
};
